/**
 * Logística → Canjes CardPlus (`/logistica/canjes-cardplus`).
 *
 * Puerto de `Cardplus::canjes` + `views/cardplus/canjes.php` + el *bundle*
 * `cardplus.canjes.bundle.js` y su endpoint (`API/Cardplus::canjes_get`).
 *
 * La pantalla del panel anterior era el mismo listado con dos fechas (del día 1 a
 * hoy, puestas al abrir), los botones *Filtrar* y *Limpiar*, y un botón *Detalles*
 * por fila que mostraba el canje y su transacción de surtidor. Aquí se conserva
 * igual, con el detalle en un diálogo en lugar de la ventana modal de antes.
 */

import {
  ArrowLeft,
  CalendarRange,
  CreditCard,
  Droplet,
  Eye,
  Filter,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatNumber } from '@/lib/utils';

import { useCanjes } from '../canjes.api';
import type { Canje, CanjesFiltros } from '../canjes.types';

const CONTROL =
  'h-12 w-full rounded-control border border-ink-200 bg-white px-4 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local en `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Rango por defecto: del día 1 a hoy, como los campos del panel anterior. */
function rangoPorDefecto(): CanjesFiltros {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    hasta: fechaLocal(hoy),
  };
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Dato del detalle. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{etiqueta}</dt>
      <dd className="text-sm text-ink-800">{valor || '—'}</dd>
    </div>
  );
}

/** Detalle de un canje y de su transacción de surtidor. */
function CanjeDetalle({ canje, onCerrar }: { canje: Canje; onCerrar: () => void }) {
  return (
    <Dialog
      open
      onClose={onCerrar}
      size="lg"
      title={`Canje ${canje.documento}`}
      description={canje.cliente || 'Cliente no informado'}
      footer={<Badge variant="outline">{canje.tarjeta || 'sin tarjeta'}</Badge>}
    >
      <div className="flex flex-col gap-5">
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-ink-900">Información del canje</h3>
          <dl className="grid gap-4 sm:grid-cols-3">
            <Dato etiqueta="Documento" valor={canje.documento} />
            <Dato etiqueta="Cliente" valor={canje.cliente} />
            <Dato etiqueta="Documento del cliente" valor={canje.documento_cliente} />
            <Dato etiqueta="Tarjeta" valor={canje.tarjeta} />
            <Dato etiqueta="Placa" valor={canje.placa} />
            <Dato etiqueta="Grifero" valor={canje.grifero ?? ''} />
            <Dato etiqueta="Motivo" valor={canje.motivo} />
            <Dato etiqueta="Fecha" valor={canje.fecha ?? ''} />
            <Dato etiqueta="Turno" valor={canje.turno_id ? `#${canje.turno_id}` : ''} />
          </dl>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            <Droplet className="size-4 text-brand-500" aria-hidden="true" />
            Transacción de surtidor
          </h3>

          {canje.transaccion ? (
            <dl className="grid gap-4 sm:grid-cols-3">
              <Dato etiqueta="Transacción" valor={`#${canje.transaccion.id}`} />
              <Dato etiqueta="Fecha" valor={canje.transaccion.fecha ?? ''} />
              <Dato etiqueta="Producto" valor={canje.transaccion.producto} />
              <Dato etiqueta="Surtidor" valor={`Surtidor ${canje.transaccion.surtidor}`} />
              <Dato etiqueta="Manguera" valor={`Manguera ${canje.transaccion.manguera}`} />
              <Dato
                etiqueta="Volumen"
                valor={`${formatNumber(canje.transaccion.volumen)} gal`}
              />
              <Dato
                etiqueta="Precio"
                valor={`${formatCurrency(canje.transaccion.precio)} / gal`}
              />
              <Dato etiqueta="Importe" valor={formatCurrency(canje.transaccion.importe)} />
            </dl>
          ) : (
            <Alert variant="info">
              Este canje no tiene una transacción de surtidor asociada (o ya no está disponible).
            </Alert>
          )}
        </section>
      </div>
    </Dialog>
  );
}

export function CanjesCardplusPage() {
  useDocumentTitle('Logística · Canjes CardPlus');

  const [desde, setDesde] = useState(rangoPorDefecto().desde ?? '');
  const [hasta, setHasta] = useState(rangoPorDefecto().hasta ?? '');
  const [filtros, setFiltros] = useState<CanjesFiltros>(rangoPorDefecto);
  const [avisoFiltro, setAvisoFiltro] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<Canje | null>(null);

  const canjes = useCanjes(filtros);
  const sinPermiso = canjes.error instanceof ApiError && canjes.error.status === 403;
  const lista = canjes.data?.canjes ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <CreditCard className="size-6 text-brand-500" aria-hidden="true" />
          Canjes CardPlus
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Canjes registrados por el servicio de CardPlus, con la transacción de surtidor que los
          originó.
        </p>
      </div>

      {canjes.data ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{canjes.data.total} canjes</Badge>
          <Badge variant="brand">
            <Droplet className="size-3" aria-hidden="true" />
            {formatNumber(canjes.data.volumen)} gal
          </Badge>
          <Badge variant="success">{formatCurrency(canjes.data.importe)}</Badge>
        </div>
      ) : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>logistics</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="canjes-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="canjes-desde"
               
                className={`${CONTROL} w-44`}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="canjes-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="canjes-hasta"
               
                className={`${CONTROL} w-44`}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                if (!desde || !hasta) {
                  setAvisoFiltro('Selecciona una fecha de inicio y una de fin.');
                  return;
                }

                setAvisoFiltro(null);
                setFiltros({ desde, hasta });
              }}
            >
              Filtrar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                setDesde('');
                setHasta('');
                setAvisoFiltro(null);
                // Sin fechas la API devuelve todo, como el botón «Limpiar» anterior.
                setFiltros({});
              }}
            >
              Limpiar
            </Button>

            {canjes.data?.rango.desde && canjes.data.rango.hasta ? (
              <Badge variant="outline" className="ml-auto">
                <CalendarRange className="size-3" aria-hidden="true" />
                {canjes.data.rango.desde} → {canjes.data.rango.hasta}
              </Badge>
            ) : null}
          </div>

          {avisoFiltro ? <Alert variant="warning">{avisoFiltro}</Alert> : null}

          {canjes.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : canjes.error ? (
            <Alert variant="error" title="No se pudieron leer los canjes">
              {mensajeDe(canjes.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay canjes en el rango seleccionado.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Documento</th>
                    <th className="py-2">Cliente</th>
                    <th className="py-2">Grifero</th>
                    <th className="py-2">Tarjeta</th>
                    <th className="py-2">Placa</th>
                    <th className="py-2">Motivo</th>
                    <th className="py-2">Fecha</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((canje) => (
                    <tr key={canje.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3">
                        <Badge variant="neutral">{canje.documento}</Badge>
                      </td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">{canje.cliente}</span>
                        <span className="block text-[11px] text-ink-400">
                          Doc: {canje.documento_cliente || '—'}
                        </span>
                      </td>
                      <td className="py-3 text-ink-600">
                        {canje.grifero ?? <span className="text-ink-300">N/A</span>}
                      </td>
                      <td className="py-3">
                        <span className="font-mono text-xs text-brand-600">{canje.tarjeta}</span>
                      </td>
                      <td className="py-3">
                        {canje.placa ? (
                          <Badge variant="neutral">{canje.placa}</Badge>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="py-3 text-ink-600">
                        {canje.motivo || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3 text-ink-600">{canje.fecha ?? '—'}</td>
                      <td className="py-3">
                        <div className="flex items-center justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Eye className="size-3.5" aria-hidden="true" />}
                            aria-label={`Ver el canje ${canje.documento}`}
                            onClick={() => setDetalle(canje)}
                          >
                            Detalles
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {detalle ? <CanjeDetalle canje={detalle} onCerrar={() => setDetalle(null)} /> : null}
    </div>
  );
}
